import { useState, useCallback } from 'react';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import {
  recalculateSingle,
  getCalculationHistory,
  type RecalculationResponse,
  type CalculationDifference,
} from '../services/calculationService';

type LookupMode = 'calculationId' | 'apptId';

export default function RecalculationPage() {
  const [mode, setMode] = useState<LookupMode>('calculationId');
  const [calculationId, setCalculationId] = useState('');
  const [apptId, setApptId] = useState('');
  const [useCurrentAgreement, setUseCurrentAgreement] = useState(true);

  // Lookup by ApptID state
  const [calculations, setCalculations] = useState<Record<string, unknown>[]>([]);
  const [selectedCalcId, setSelectedCalcId] = useState<number | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState('');

  // Recalculation state
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [recalcResult, setRecalcResult] = useState<RecalculationResponse | null>(null);
  const [recalcError, setRecalcError] = useState('');

  const resetResults = useCallback(() => {
    setRecalcResult(null);
    setRecalcError('');
  }, []);

  // Fetch calculation history by ApptID
  const handleFetchHistory = useCallback(async () => {
    if (!apptId.trim()) return;
    setIsLoadingHistory(true);
    setHistoryError('');
    setCalculations([]);
    setSelectedCalcId(null);
    resetResults();

    try {
      const data = await getCalculationHistory(apptId.trim());
      if (data.calculations.length === 0) {
        setHistoryError('No calculations found for this Appointment ID.');
      } else {
        setCalculations(data.calculations);
      }
    } catch (err: any) {
      setHistoryError(
        err?.response?.data?.error || err?.message || 'Failed to fetch calculation history.'
      );
    } finally {
      setIsLoadingHistory(false);
    }
  }, [apptId, resetResults]);

  // Execute recalculation
  const handleRecalculate = useCallback(async () => {
    const calcId =
      mode === 'calculationId' ? Number(calculationId) : selectedCalcId;
    if (!calcId || isNaN(calcId)) return;

    setIsRecalculating(true);
    setRecalcError('');
    setRecalcResult(null);

    try {
      const result = await recalculateSingle(calcId, useCurrentAgreement);
      setRecalcResult(result);
    } catch (err: any) {
      const data = err?.response?.data;
      const msg =
        data?.error ||
        (Array.isArray(data?.errors) ? data.errors.join('; ') : null) ||
        err?.message ||
        'Recalculation failed.';
      setRecalcError(msg);
    } finally {
      setIsRecalculating(false);
    }
  }, [mode, calculationId, selectedCalcId, useCurrentAgreement]);

  const canRecalculate =
    mode === 'calculationId'
      ? calculationId.trim() && !isNaN(Number(calculationId))
      : selectedCalcId !== null;

  return (
    <div className="recalculation-page">
      <div className="page-header">
        <h2>Recalculation</h2>
      </div>

      {/* Lookup Mode Toggle */}
      <section className="recalc-section">
        <div className="recalc-mode-toggle">
          <Button
            variant={mode === 'calculationId' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => {
              setMode('calculationId');
              resetResults();
              setCalculations([]);
              setSelectedCalcId(null);
            }}
          >
            By Calculation ID
          </Button>
          <Button
            variant={mode === 'apptId' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => {
              setMode('apptId');
              resetResults();
            }}
          >
            By Appointment ID
          </Button>
        </div>

        {mode === 'calculationId' && (
          <div className="recalc-input-row">
            <Input
              label="Calculation ID"
              type="number"
              value={calculationId}
              onChange={(e) => {
                setCalculationId(e.target.value);
                resetResults();
              }}
              placeholder="Enter Calculation ID"
            />
          </div>
        )}

        {mode === 'apptId' && (
          <>
            <div className="recalc-input-row">
              <Input
                label="Appointment ID"
                value={apptId}
                onChange={(e) => setApptId(e.target.value)}
                placeholder="Enter ApptID"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleFetchHistory();
                }}
              />
              <Button
                onClick={handleFetchHistory}
                isLoading={isLoadingHistory}
                disabled={!apptId.trim() || isLoadingHistory}
              >
                Find Calculations
              </Button>
            </div>
            {historyError && <Alert type="error">{historyError}</Alert>}
            {calculations.length > 0 && (
              <div className="recalc-history-list">
                <p className="recalc-history-label">Select a calculation to recalculate:</p>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>ID</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Hospital Share</th>
                      <th>AR/AP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calculations.map((calc) => {
                      const id = Number(calc.calculation_id);
                      return (
                        <tr
                          key={id}
                          className={selectedCalcId === id ? 'row-selected' : ''}
                          onClick={() => {
                            setSelectedCalcId(id);
                            resetResults();
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>
                            <input
                              type="radio"
                              name="calc-select"
                              checked={selectedCalcId === id}
                              onChange={() => {
                                setSelectedCalcId(id);
                                resetResults();
                              }}
                              aria-label={`Select calculation ${id}`}
                            />
                          </td>
                          <td>{id}</td>
                          <td>
                            {calc.calculated_at
                              ? new Date(String(calc.calculated_at)).toLocaleString()
                              : '—'}
                          </td>
                          <td>
                            <span
                              className={`status-badge ${
                                calc.validation_status === 'OK'
                                  ? 'status-active'
                                  : calc.validation_status === 'NOT_OK'
                                  ? 'status-inactive'
                                  : ''
                              }`}
                            >
                              {String(calc.validation_status || 'PENDING')}
                            </span>
                          </td>
                          <td>{formatNum(calc.hospital_share)}</td>
                          <td>{formatNum(calc.ar_ap)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>

      {/* Options & Recalculate Button */}
      <section className="recalc-section">
        <label className="recalc-checkbox">
          <input
            type="checkbox"
            checked={useCurrentAgreement}
            onChange={(e) => setUseCurrentAgreement(e.target.checked)}
          />
          Use current active agreement (otherwise uses original agreement)
        </label>
        <div className="recalc-actions">
          <Button
            size="lg"
            onClick={handleRecalculate}
            isLoading={isRecalculating}
            disabled={!canRecalculate || isRecalculating}
          >
            Recalculate
          </Button>
        </div>
        {isRecalculating && <LoadingSpinner size="sm" message="Recalculating..." />}
        {recalcError && <Alert type="error">{recalcError}</Alert>}
      </section>

      {/* Comparison View */}
      {recalcResult && (
        <section className="recalc-section" aria-label="Recalculation comparison">
          <h3>Comparison</h3>
          {!recalcResult.success && recalcResult.errors && (
            <Alert type="warning">
              Recalculation completed with errors:{' '}
              {recalcResult.errors.join('; ')}
            </Alert>
          )}

          {/* Agreement version info */}
          <div className="recalc-version-info">
            <span>
              Old Agreement: v
              {String(recalcResult.oldCalculation.agreement_version ?? '?')}
            </span>
            <span>
              New Agreement: v
              {String(recalcResult.newCalculation.agreement_version ?? '?')}
            </span>
          </div>

          {/* Summary cards */}
          <div className="recalc-summary-cards">
            <ComparisonCard
              label="Hospital Share"
              oldVal={recalcResult.oldCalculation.hospital_share}
              newVal={recalcResult.newCalculation.hospital_share}
            />
            <ComparisonCard
              label="AR/AP"
              oldVal={recalcResult.oldCalculation.ar_ap}
              newVal={recalcResult.newCalculation.ar_ap}
            />
          </div>

          {/* Detailed differences table */}
          {recalcResult.differences.length > 0 ? (
            <div className="recalc-diff-table-wrap">
              <h4>Changed Values</h4>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Old Value</th>
                    <th>New Value</th>
                    <th>Change</th>
                  </tr>
                </thead>
                <tbody>
                  {recalcResult.differences.map((diff, i) => (
                    <tr key={i} className="row-changed">
                      <td>{diff.field}</td>
                      <td>{formatVal(diff.oldValue)}</td>
                      <td>{formatVal(diff.newValue)}</td>
                      <td>{formatChange(diff)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Alert type="info">No values changed between old and new calculation.</Alert>
          )}

          {/* Full side-by-side for calculated parameters */}
          <ParameterComparison
            label="Calculated Parameters"
            oldData={recalcResult.oldCalculation.calculated_parameter_values as Record<string, unknown> | undefined}
            newData={recalcResult.newCalculation.calculated_parameter_values as Record<string, unknown> | undefined}
          />
        </section>
      )}
    </div>
  );
}

// --- Helper Components ---

function ComparisonCard({
  label,
  oldVal,
  newVal,
}: {
  label: string;
  oldVal: unknown;
  newVal: unknown;
}) {
  const oldNum = Number(oldVal) || 0;
  const newNum = Number(newVal) || 0;
  const diff = newNum - oldNum;
  const changed = Math.abs(diff) > 0.001;

  return (
    <div className={`recalc-compare-card ${changed ? 'recalc-compare-changed' : ''}`}>
      <span className="recalc-compare-label">{label}</span>
      <div className="recalc-compare-values">
        <div className="recalc-compare-old">
          <span className="recalc-compare-sublabel">Old</span>
          <span className="recalc-compare-num">{formatNum(oldVal)}</span>
        </div>
        <span className="recalc-compare-arrow">→</span>
        <div className="recalc-compare-new">
          <span className="recalc-compare-sublabel">New</span>
          <span className="recalc-compare-num">{formatNum(newVal)}</span>
        </div>
      </div>
      {changed && (
        <span className={`recalc-compare-diff ${diff > 0 ? 'diff-positive' : 'diff-negative'}`}>
          {diff > 0 ? '+' : ''}
          {diff.toFixed(2)}
          {oldNum !== 0 && ` (${((diff / Math.abs(oldNum)) * 100).toFixed(1)}%)`}
        </span>
      )}
    </div>
  );
}

function ParameterComparison({
  label,
  oldData,
  newData,
}: {
  label: string;
  oldData?: Record<string, unknown>;
  newData?: Record<string, unknown>;
}) {
  if (!oldData && !newData) return null;
  const allKeys = Array.from(
    new Set([...Object.keys(oldData || {}), ...Object.keys(newData || {})])
  );
  if (allKeys.length === 0) return null;

  return (
    <div className="recalc-param-comparison">
      <h4>{label}</h4>
      <table className="data-table">
        <thead>
          <tr>
            <th>Parameter</th>
            <th>Old Value</th>
            <th>New Value</th>
          </tr>
        </thead>
        <tbody>
          {allKeys.map((key) => {
            const ov = oldData?.[key];
            const nv = newData?.[key];
            const changed = String(ov) !== String(nv);
            return (
              <tr key={key} className={changed ? 'row-changed' : ''}>
                <td>{key}</td>
                <td>{formatVal(ov)}</td>
                <td>{formatVal(nv)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// --- Formatting helpers ---

function formatNum(val: unknown): string {
  if (val == null) return '—';
  const n = Number(val);
  if (isNaN(n)) return String(val);
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatVal(val: unknown): string {
  if (val == null) return '—';
  if (typeof val === 'number') return formatNum(val);
  return String(val);
}

function formatChange(diff: CalculationDifference): string {
  if (diff.absoluteChange != null && diff.percentChange != null) {
    const sign = diff.absoluteChange > 0 ? '+' : '';
    return `${sign}${diff.absoluteChange.toFixed(2)} (${diff.percentChange.toFixed(1)}%)`;
  }
  if (diff.absoluteChange != null) {
    const sign = diff.absoluteChange > 0 ? '+' : '';
    return `${sign}${diff.absoluteChange.toFixed(2)}`;
  }
  if (diff.percentChange != null) {
    return `${diff.percentChange.toFixed(1)}%`;
  }
  return 'changed';
}
